import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getElection, submitVote } from '../services/api';
import './Pages.css';

const VotePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // For different voting types
  const [selectedCandidate, setSelectedCandidate] = useState(null); // Single Choice
  const [selectedCandidates, setSelectedCandidates] = useState([]); // Multi Choice
  const [rankedCandidates, setRankedCandidates] = useState([]); // Ranked Choice

  useEffect(() => {
    const fetchElection = async () => {
      try {
        setLoading(true);
        const response = await getElection(id);
        const electionData = response.data.election;
        setElection(electionData);
        
        // Initialize ranked candidates array if needed
        if (electionData.election_type === 3) {
          setRankedCandidates(new Array(electionData.candidates.length).fill(0));
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching election:', err);
        setError('Failed to load election details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchElection();
  }, [id]);

  const handleSingleChoiceSelect = (candidateId) => {
    setSelectedCandidate(candidateId);
  };

  const handleMultiChoiceSelect = (candidateId) => {
    if (selectedCandidates.includes(candidateId)) {
      setSelectedCandidates(selectedCandidates.filter(id => id !== candidateId));
    } else {
      if (selectedCandidates.length < election.max_selections) {
        setSelectedCandidates([...selectedCandidates, candidateId]);
      }
    }
  };

  const handleRankChange = (candidateId, rank) => {
    // Check if this rank is already assigned
    const currentIndexWithRank = rankedCandidates.findIndex(r => r === rank);
    
    // Copy current rankings
    const newRankings = [...rankedCandidates];
    
    // Get index of the candidate we're changing
    const candidateIndex = election.candidates.findIndex(c => c.id === candidateId);
    
    // If this rank is already assigned to another candidate, clear it
    if (currentIndexWithRank !== -1 && currentIndexWithRank !== candidateIndex) {
      newRankings[currentIndexWithRank] = 0;
    }
    
    // Set the new rank for this candidate
    newRankings[candidateIndex] = rank;
    
    setRankedCandidates(newRankings);
  };

  const handleSubmitVote = async () => {
    let votes = [];
    
    // Prepare votes array based on election type
    if (election.election_type === 1) { // Single Choice
      if (!selectedCandidate) {
        setError('Please select a candidate');
        return;
      }
      votes = [selectedCandidate, 0, 0, 0, 0];
    } 
    else if (election.election_type === 2) { // Multi Choice
      if (selectedCandidates.length === 0) {
        setError('Please select at least one candidate');
        return;
      }
      // Pad with zeros to make exactly 5 elements
      votes = [...selectedCandidates].slice(0, 5);
      while (votes.length < 5) votes.push(0);
    } 
    else if (election.election_type === 3) { // Ranked Choice
      const selectedRanks = rankedCandidates.filter(rank => rank > 0);
      
      if (selectedRanks.length === 0) {
        setError('Please rank at least one candidate');
        return;
      }
      
      // Convert candidate indexes to candidate IDs with their rankings
      votes = election.candidates.map((candidate, index) => {
        const rank = rankedCandidates[index];
        return rank > 0 ? candidate.id : 0;
      }).slice(0, 5);
      
      // Pad with zeros to make exactly 5 elements
      while (votes.length < 5) votes.push(0);
    }

    setSubmitting(true);
    setError(null);

    try {
      await submitVote(election.id, votes);
      setSuccess(true);
      
      // Redirect to election details after successful vote
      setTimeout(() => {
        navigate(`/elections/${election.id}`);
      }, 2000);
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError(err.response?.data?.message || 'Failed to submit vote. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loader"></div>
        <p>Loading election details...</p>
      </div>
    );
  }

  if (error && !election) {
    return (
      <div className="error-message">
        <h2>Error</h2>
        <p>{error}</p>
        <div className="error-actions">
          <button 
            className="btn btn-outline" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
          <Link to="/elections" className="btn btn-primary">
            Back to Elections
          </Link>
        </div>
      </div>
    );
  }

  if (!election) {
    return (
      <div className="not-found">
        <h2>Election Not Found</h2>
        <p>The election you're looking for doesn't exist or has been removed.</p>
        <Link to="/elections" className="btn btn-primary">
          View All Elections
        </Link>
      </div>
    );
  }

  // Check if voting is allowed
  const now = new Date();
  if (election.start_time && new Date(election.start_time) > now) {
    return (
      <div className="vote-page">
        <h1>Voting Not Started</h1>
        <p>Voting for this election hasn't started yet.</p>
        <p>Start time: {new Date(election.start_time).toLocaleString()}</p>
        <Link to={`/elections/${election.id}`} className="btn btn-primary">
          Back to Election Details
        </Link>
      </div>
    );
  }

  if (election.end_time && new Date(election.end_time) < now) {
    return (
      <div className="vote-page">
        <h1>Voting Ended</h1>
        <p>Voting for this election has ended.</p>
        <div className="vote-actions">
          <Link to={`/elections/${election.id}/results`} className="btn btn-primary">
            View Results
          </Link>
          <Link to={`/elections/${election.id}`} className="btn btn-outline">
            Back to Election Details
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="vote-success">
        <div className="success-icon">✓</div>
        <h1>Vote Submitted Successfully!</h1>
        <p>Your encrypted vote has been recorded.</p>
        <p>Redirecting to election details...</p>
      </div>
    );
  }

  return (
    <div className="vote-page">
      <div className="page-header">
        <button 
          className="btn-back" 
          onClick={() => navigate(-1)}
        >
          &larr; Back
        </button>
        <h1>Vote: {election.title}</h1>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="vote-instructions card">
        <h2>Voting Instructions</h2>
        
        {election.election_type === 1 && (
          <p>Select <strong>one</strong> candidate by clicking on their name.</p>
        )}
        
        {election.election_type === 2 && (
          <p>Select up to <strong>{election.max_selections}</strong> candidates by checking the boxes.</p>
        )}
        
        {election.election_type === 3 && (
          <p>Rank the candidates by selecting their position (1 being your top choice).</p>
        )}
        
        <div className="security-info">
          <p><strong>Privacy Notice:</strong> Your vote will be encrypted and cannot be linked back to you.</p>
        </div>
      </div>

      <div className="vote-form card">
        <h2>Candidates</h2>
        
        {election.election_type === 1 && (
          <div className="single-choice-candidates">
            {election.candidates.map((candidate) => (
              <div 
                key={candidate.id}
                className={`candidate-option ${selectedCandidate === candidate.id ? 'selected' : ''}`}
                onClick={() => handleSingleChoiceSelect(candidate.id)}
              >
                <div className="candidate-select">
                  <div className="radio-button">
                    <div className="radio-inner"></div>
                  </div>
                </div>
                <div className="candidate-info">
                  <div className="candidate-name">{candidate.name}</div>
                  {candidate.description && (
                    <div className="candidate-description">{candidate.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {election.election_type === 2 && (
          <div className="multi-choice-candidates">
            {election.candidates.map((candidate) => (
              <div 
                key={candidate.id}
                className={`candidate-option ${selectedCandidates.includes(candidate.id) ? 'selected' : ''}`}
                onClick={() => handleMultiChoiceSelect(candidate.id)}
              >
                <div className="candidate-select">
                  <div className="checkbox">
                    <div className="checkbox-inner"></div>
                  </div>
                </div>
                <div className="candidate-info">
                  <div className="candidate-name">{candidate.name}</div>
                  {candidate.description && (
                    <div className="candidate-description">{candidate.description}</div>
                  )}
                </div>
              </div>
            ))}
            
            <div className="selection-count">
              Selected: {selectedCandidates.length} / {election.max_selections}
            </div>
          </div>
        )}
        
        {election.election_type === 3 && (
          <div className="ranked-choice-candidates">
            {election.candidates.map((candidate, index) => (
              <div key={candidate.id} className="candidate-rank-option">
                <div className="candidate-info">
                  <div className="candidate-name">{candidate.name}</div>
                  {candidate.description && (
                    <div className="candidate-description">{candidate.description}</div>
                  )}
                </div>
                <div className="rank-selector">
                  <select 
                    value={rankedCandidates[index] || ''}
                    onChange={(e) => handleRankChange(candidate.id, parseInt(e.target.value) || 0)}
                  >
                    <option value="">Not ranked</option>
                    {Array.from({ length: Math.min(election.max_selections, election.candidates.length) }, (_, i) => (
                      <option key={i+1} value={i+1}>Rank {i+1}</option>
                    ))}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="vote-actions">
          <button 
            className="btn btn-outline"
            onClick={() => navigate(`/elections/${election.id}`)}
          >
            Cancel
          </button>
          
          <button 
            className="btn btn-primary"
            onClick={handleSubmitVote}
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Vote'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VotePage;